import { useEffect, useRef, useState } from "react";
import { API_BASE_URL, apiListInbox, apiUnreadCount } from "../../api/client";
import { safeGetItem } from "../../utils/safe-storage";
import { Badge } from "../ui/avatar";
import { Icon } from "../icons/Icon";
import { EmptyState, Skeleton } from "../ui/feedback";
import "../../styles/components/layout.css";

interface NotifItem {
  id: string;
  title: string;
  content?: string;
  severity?: string;
  ts?: string;
}

const SEVERITY_TONE: Record<string, string> = {
  CRITICAL: "red",
  HIGH: "amber",
  WARNING: "amber",
  INFO: "blue",
  LOW: "gray",
};

export interface BellNotificationProps {
  onOpenAll: () => void;
}

/**
 * 通知铃铛：未读数 + 最近 6 条 + SSE 实时增量。
 * 数据来源：`GET /notifications/unread-count`、`GET /notifications/inbox`，实时通道为 SSE
 * （非 WebSocket：后端 /sse/notifications 已具备，WebSocket 通道尚未接入）。
 */
export default function BellNotification({ onOpenAll }: BellNotificationProps) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotifItem[]>([]);
  const [loading, setLoading] = useState(true);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [countResp, inboxResp] = await Promise.all([apiUnreadCount(), apiListInbox({ page: 1, page_size: 6 })]);
        if (!alive) return;
        // 网关未读字段是 `unread`（inbox 响应同名字段）；旧代码只读 `count` → 铃铛恒 0
        const count = countResp.data?.data?.count ?? countResp.data?.data?.unread ?? countResp.data?.unread ?? countResp.data?.count ?? 0;
        // 未读接口不可用时，退回 inbox 的 unread 字段，避免"有未读但铃铛是 0"
        const inboxBody = (inboxResp.data?.data ?? {}) as Record<string, unknown>;
        const fallbackUnread = Number(inboxBody.unread ?? 0);
        setUnread(Number(count) || fallbackUnread || 0);
        const list = inboxBody.items ?? (inboxResp.data as Record<string, unknown> | undefined)?.items ?? [];
        setItems(
          (Array.isArray(list) ? list : []).map((n: Record<string, unknown>, index: number) => ({
            id: String(n.id ?? index),
            title: String(n.title ?? "系统通知"),
            // 消息正文在 inbox 里叫 `body`（旧代码读 content → 内容恒空）
            content: n.body ? String(n.body) : n.content ? String(n.content) : undefined,
            severity: n.severity ? String(n.severity) : undefined,
            ts: n.created_at ? String(n.created_at) : undefined,
          })),
        );
      } catch (err) {
        console.error("[bell] load notifications failed", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const token = safeGetItem("accessToken");
    if (!token) return;

    // 未读数一律以服务端为准：SSE 事件只用来**触发重新拉取**。
    // 旧实现是本地 `setUnread(n => n + 1)`，一旦事件重复或丢失就与服务端永久漂移
    // （线上曾因扫描器每 2 分钟重推同一批告警，前端计数越加越大）。
    const refreshUnread = async () => {
      try {
        const resp = await apiUnreadCount();
        const body = (resp.data?.data ?? {}) as Record<string, unknown>;
        const value = body.count ?? body.unread ?? resp.data?.unread ?? resp.data?.count;
        if (value !== undefined) setUnread(Number(value) || 0);
      } catch (err) {
        console.warn("[bell] refresh unread count failed", err);
      }
    };

    const source = new EventSource(`${API_BASE_URL}/sse/notifications?token=${encodeURIComponent(token)}`);
    source.addEventListener("notification", (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data) as Record<string, unknown>;
        const item: NotifItem = {
          id: String(payload.id ?? Date.now()),
          title: String(payload.title ?? "新通知"),
          content: payload.content ? String(payload.content) : undefined,
          severity: payload.severity ? String(payload.severity) : undefined,
          ts: new Date().toISOString(),
        };
        setItems((prev) => [item, ...prev.filter((p) => p.id !== item.id)].slice(0, 50));
        void refreshUnread();
      } catch (err) {
        console.error("[bell] parse sse notification failed", err);
      }
    });
    source.onerror = () => {
      // EventSource 会自动重连；仅在控制台留痕，避免刷屏
      console.warn("[bell] notification stream disconnected, retrying");
    };
    return () => source.close();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="bell-wrap" ref={wrapRef}>
      <Badge count={unread} max={99}>
        <button
          type="button"
          className="topnav-icon-btn"
          aria-label={`通知${unread > 0 ? `（${unread} 条未读）` : ""}`}
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name="bell" size={17} />
        </button>
      </Badge>

      {open ? (
        <div className="bell-panel">
          <div className="bell-head">
            <span className="bell-head-title">通知</span>
            {unread > 0 ? <span className="bell-head-count">{unread} 条未读</span> : null}
          </div>
          <div className="bell-list">
            {loading ? (
              <div className="bell-loading">
                <Skeleton width="70%" height={13} />
                <Skeleton width="85%" height={13} />
                <Skeleton width="55%" height={13} />
              </div>
            ) : items.length === 0 ? (
              <EmptyState compact icon="bell" title="暂无新通知" description="低库存、逾期采购、设备异常会在这里提醒。" />
            ) : (
              items.slice(0, 6).map((item) => (
                <button
                  type="button"
                  className="bell-item"
                  key={item.id}
                  onClick={() => {
                    setOpen(false);
                    onOpenAll();
                  }}
                >
                  <span className={`bell-dot bell-dot-${SEVERITY_TONE[item.severity ?? "INFO"] ?? "blue"}`} />
                  <span className="bell-item-main">
                    <span className="bell-item-title">{item.title}</span>
                    {item.content ? <span className="bell-item-content">{item.content}</span> : null}
                  </span>
                </button>
              ))
            )}
          </div>
          <div className="bell-foot">
            <button
              type="button"
              className="bell-more"
              onClick={() => {
                setOpen(false);
                setUnread(0);
                onOpenAll();
              }}
            >
              查看全部消息
              <Icon name="arrow-right" size={13} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
